// Daily Activity Digest — presentation renderer ("Option C: Executive Summary").
// Renders a digest payload as company-wise sections: a row of glance tiles
// followed by a full activity matrix table. Used inside a dialog from the
// Notifications inbox and from the Admin → Daily Digest preview.
import React from 'react';
import { formatINR, formatINRCompact, formatGrams, formatCarats } from '@/lib/format';
import {
  Building2, CalendarDays, Info, TrendingUp,
  FileText, PencilRuler, ClipboardList, Coins, ShieldCheck, Gem, RotateCcw, ShoppingCart, ArrowLeftRight, Sparkles,
} from 'lucide-react';

// Format an ISO 'YYYY-MM-DD' date as a compact 'D Mon' label for the trend chip.
const SPARK_MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const fmtSpark = (iso) => {
  if (!iso) return '';
  const parts = String(iso).split('-').map(Number);
  const [y, m, d] = parts;
  if (!m || !d) return '';
  return `${d} ${SPARK_MON[m - 1]}`;
};

// Per-subject colour + icon for the glance tiles. Literal class strings so
// Tailwind's JIT keeps them in the build.
const TILE_STYLE = {
  order_forms:        { icon: FileText,      cls: 'bg-white border-indigo-300 text-indigo-700',    ring: 'hover:border-indigo-500 focus-visible:ring-indigo-400' },
  cad_confirmed:      { icon: PencilRuler,   cls: 'bg-white border-violet-300 text-violet-700',    ring: 'hover:border-violet-500 focus-visible:ring-violet-400' },
  requirement_raised: { icon: ClipboardList, cls: 'bg-white border-amber-300 text-amber-700',      ring: 'hover:border-amber-500 focus-visible:ring-amber-400' },
  raw_material_issued:{ icon: Coins,         cls: 'bg-white border-sky-300 text-sky-700',          ring: 'hover:border-sky-500 focus-visible:ring-sky-400' },
  setting_done:       { icon: Sparkles,      cls: 'bg-white border-orange-300 text-orange-700',    ring: 'hover:border-orange-500 focus-visible:ring-orange-400' },
  qc_done:            { icon: ShieldCheck,   cls: 'bg-white border-teal-300 text-teal-700',        ring: 'hover:border-teal-500 focus-visible:ring-teal-400' },
  jewellery_received: { icon: Gem,           cls: 'bg-white border-emerald-300 text-emerald-700',  ring: 'hover:border-emerald-500 focus-visible:ring-emerald-400' },
  goldsmith_returns:  { icon: RotateCcw,     cls: 'bg-white border-rose-300 text-rose-700',        ring: 'hover:border-rose-500 focus-visible:ring-rose-400' },
  purchases:          { icon: ShoppingCart,  cls: 'bg-white border-cyan-300 text-cyan-700',        ring: 'hover:border-cyan-500 focus-visible:ring-cyan-400' },
  transfers:          { icon: ArrowLeftRight,cls: 'bg-white border-fuchsia-300 text-fuchsia-700',  ring: 'hover:border-fuchsia-500 focus-visible:ring-fuchsia-400' },
};
const TILE_STYLE_FALLBACK = { icon: FileText, cls: 'bg-white border-[var(--divider)] text-[var(--ink-navy)]', ring: 'hover:border-[var(--ink-navy)] focus-visible:ring-[var(--diamond-icon)]' };

// Solid, filled badge colour per subject — the darker shade of the matching
// glance-tile hue above. Used for the Count pill when count >= 1. Literal
// class strings so Tailwind's JIT keeps them in the build.
const COUNT_BADGE = {
  order_forms:         'bg-indigo-600',
  cad_confirmed:       'bg-violet-600',
  requirement_raised:  'bg-amber-600',
  raw_material_issued: 'bg-sky-600',
  setting_done:        'bg-orange-600',
  qc_done:             'bg-teal-600',
  jewellery_received:  'bg-emerald-600',
  goldsmith_returns:   'bg-rose-600',
  purchases:           'bg-cyan-600',
  transfers:           'bg-fuchsia-600',
};

const MATERIAL_COLS = [
  { key: 'gold', label: 'Gold (g)' },
  { key: 'diamond', label: 'Diamond (ct · pcs)' },
  { key: 'cs', label: 'Colour Stone (ct · pcs)' },
  { key: 'pearl', label: 'Pearl (ct · pcs)' },
];

const DIM = <span className="text-slate-300">—</span>;

// ---- cell renderers --------------------------------------------------------
const goldCell = (m) => {
  const g = Number(m?.gold_g || 0);
  const mt = Number(m?.mounting_g || 0);
  if (!g && !mt) return DIM;
  return (
    <span className="font-mono tabular-nums">
      {g ? formatGrams(g) : '0.000'}
      {mt > 0 && <span className="block text-[10px] text-slate-400">+{formatGrams(mt)} mtg</span>}
    </span>
  );
};
const caratCell = (ct, pcs) => {
  ct = Number(ct || 0); pcs = Number(pcs || 0);
  if (!ct && !pcs) return DIM;
  return <span className="font-mono tabular-nums">{formatCarats(ct)} · {pcs}</span>;
};
const valueCell = (val, canView) => {
  if (!canView) return <span className="font-mono tabular-nums text-slate-400">₹ —</span>;
  if (val === null || val === undefined) return DIM;
  return <span className="font-mono tabular-nums">{formatINR(val)}</span>;
};

const CountPill = ({ n, subjectKey }) => {
  const count = Number(n ?? 0);
  // Zero → muted grey; 1+ → the subject's tile hue (filled). White number always.
  const bg = count > 0 ? (COUNT_BADGE[subjectKey] || 'bg-[var(--ink-navy)]') : 'bg-slate-400';
  return (
    <span className={`inline-flex min-w-[26px] items-center justify-center rounded-full ${bg} px-2 py-0.5 text-[11px] font-semibold text-white tabular-nums`} data-testid="digest-count-pill">
      {n ?? 0}
    </span>
  );
};

// Premium gradient-area momentum sparkline (Option A). Inherits the tile's hue
// via `currentColor`: a smooth curve over a fading gradient fill, finished with
// a soft glowing end-point marking the selected "To" date.
function Sparkline({ data }) {
  const gid = React.useId();
  if (!Array.isArray(data) || data.length < 2) return null;
  const W = 150, H = 34, PADX = 3, PADY = 6;
  const n = data.length;
  const max = Math.max(1, ...data);
  const min = Math.min(...data);
  const span = Math.max(1, max - min);
  const stepX = (W - PADX * 2) / (n - 1);
  const xy = (v, i) => [PADX + i * stepX, H - PADY - ((v - min) / span) * (H - PADY * 2)];
  const P = data.map((v, i) => xy(v, i));

  // Catmull-Rom → cubic bézier smoothing for a soft, premium curve.
  let line = `M${P[0][0].toFixed(2)},${P[0][1].toFixed(2)}`;
  for (let i = 0; i < P.length - 1; i++) {
    const p0 = P[i - 1] || P[i], p1 = P[i], p2 = P[i + 1], p3 = P[i + 2] || p2;
    const c1x = p1[0] + (p2[0] - p0[0]) / 6, c1y = p1[1] + (p2[1] - p0[1]) / 6;
    const c2x = p2[0] - (p3[0] - p1[0]) / 6, c2y = p2[1] - (p3[1] - p1[1]) / 6;
    line += `C${c1x.toFixed(2)},${c1y.toFixed(2)} ${c2x.toFixed(2)},${c2y.toFixed(2)} ${p2[0].toFixed(2)},${p2[1].toFixed(2)}`;
  }
  const area = `${line}L${P[n - 1][0].toFixed(2)},${H}L${P[0][0].toFixed(2)},${H}Z`;
  const end = P[n - 1];
  const flat = max === 1 && data.every((v) => !v);

  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="mt-1.5 block h-[34px] w-full" style={{ opacity: flat ? 0.4 : 1 }} aria-hidden="true" data-testid="digest-tile-sparkline">
      <defs>
        <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="currentColor" stopOpacity="0.26" />
          <stop offset="1" stopColor="currentColor" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#${gid})`} />
      <path d={line} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      {!flat && (
        <g>
          <circle cx={end[0]} cy={end[1]} r="4.2" fill="currentColor" opacity="0.16" />
          <circle cx={end[0]} cy={end[1]} r="2" fill="currentColor" />
          <circle cx={end[0]} cy={end[1]} r="0.9" fill="#fff" />
        </g>
      )}
    </svg>
  );
}

// Build the ordered list of table rows for one company from its data + the
// subscribed subjects. `purchases` expands to two rows.
function buildRows(subjects, data) {
  const rows = [];
  for (const s of subjects) {
    const d = data?.[s.key];
    if (s.key === 'purchases') {
      const p = d?.purchases || {}; const pr = d?.purchase_returns || {};
      rows.push({ id: 'purchases', subjectKey: 'purchases', label: 'Purchases', m: p });
      rows.push({ id: 'purchase_returns', subjectKey: 'purchases', label: 'Purchase Returns', m: pr });
    } else if (s.key === 'order_forms' || s.key === 'setting_done' || s.key === 'qc_done') {
      rows.push({ id: s.key, subjectKey: s.key, label: s.label, m: d || {}, countOnly: true });
    } else if (s.key === 'cad_confirmed' || s.key === 'requirement_raised') {
      rows.push({ id: s.key, subjectKey: s.key, label: s.label, m: d || {}, noValue: true });
    } else {
      rows.push({ id: s.key, subjectKey: s.key, label: s.label, m: d || {} });
    }
  }
  return rows;
}

// Glance tiles (one per subscribed subject).
function Tiles({ subjects, data, canView, onDrill, company, sparklines }) {
  const tiles = subjects.map((s) => {
    const d = data?.[s.key] || {};
    if (s.key === 'purchases') {
      const n = d?.purchases?.count || 0; const r = d?.purchase_returns?.count || 0;
      return { key: s.key, lab: 'Purch / Ret', v: `${n}/${r}`, sub: canView ? d?.purchases?.value : null };
    }
    return { key: s.key, lab: TILE_LABEL[s.key] || s.label, v: String(d?.count ?? 0), sub: canView ? d?.value : null };
  });
  return (
    <div className="grid gap-2 mb-3" style={{ gridTemplateColumns: `repeat(${Math.max(tiles.length, 1)}, minmax(0, 1fr))` }}>
      {tiles.map((t) => {
        const st = TILE_STYLE[t.key] || TILE_STYLE_FALLBACK;
        const Icon = st.icon;
        return (
          <button
            type="button"
            key={t.key}
            onClick={onDrill ? () => onDrill(t.key, company) : undefined}
            className={`rounded-xl border px-3 py-2.5 text-center ${st.cls} ${onDrill ? `cursor-pointer hover:shadow-sm transition-shadow focus-visible:outline-none focus-visible:ring-2 ${st.ring}` : 'cursor-default'}`}
            data-testid={`digest-tile-${t.key}`}
          >
            <div className="flex items-center justify-center gap-1 min-h-[24px]">
              <Icon size={13} className="opacity-80 shrink-0" />
              <span className="text-[10px] font-semibold uppercase tracking-wide leading-tight">{t.lab}</span>
            </div>
            <div className="mt-1 font-mono text-[22px] font-semibold tabular-nums">{t.v}</div>
            <Sparkline data={sparklines?.[company?.company_id]?.[t.key]} />
            {t.sub != null && Number(t.sub) > 0 && (
              <div className="text-[11px] font-semibold opacity-90">{formatINRCompact(t.sub)}</div>
            )}
          </button>
        );
      })}
    </div>
  );
}

const TILE_LABEL = {
  order_forms: 'Order Forms',
  cad_confirmed: 'CAD Confirmed',
  requirement_raised: 'Requirement',
  raw_material_issued: 'RM Issued',
  setting_done: 'Setting',
  qc_done: 'QC Done',
  jewellery_received: 'Jewellery Recd',
  goldsmith_returns: 'Returns',
  transfers: 'Transfers',
};

function CompanySection({ company, subjects, canView, onDrill, sparklines }) {
  const data = company.data || {};
  const rows = buildRows(subjects, data);
  return (
    <div className="mb-5" data-testid={`digest-company-${company.company_id || 'na'}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="inline-flex h-2 w-2 rounded-full bg-[var(--gold-accent)]" />
        <Building2 size={14} className="text-[var(--goldsmith-icon)]" />
        <span className="text-[13px] font-bold uppercase tracking-[0.04em] text-[var(--ink-navy)]">{company.company_name}</span>
      </div>
      <Tiles subjects={subjects} data={data} canView={canView} onDrill={onDrill} company={company} sparklines={sparklines} />
      <div className="overflow-x-auto rounded-xl border border-[var(--divider)]">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="bg-[var(--diamond-bg)] text-[var(--diamond-icon)]">
              <th className="text-left px-3 py-2 font-semibold text-[11px] uppercase tracking-wide">Activity</th>
              <th className="text-center px-3 py-2 font-semibold text-[11px] uppercase tracking-wide">Count</th>
              {MATERIAL_COLS.map((c) => (
                <th key={c.key} className="text-right px-3 py-2 font-semibold text-[11px] uppercase tracking-wide whitespace-nowrap">{c.label}</th>
              ))}
              <th className="text-right px-3 py-2 font-semibold text-[11px] uppercase tracking-wide">Value (₹)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr
                key={r.id}
                className={`${i % 2 ? 'bg-[var(--paper-warm)]' : 'bg-white'} ${onDrill ? 'cursor-pointer hover:bg-[var(--diamond-bg)] transition-colors' : ''}`}
                onClick={onDrill ? () => onDrill(r.subjectKey, company) : undefined}
                data-testid={`digest-row-${r.id}`}
              >
                <td className="px-3 py-2 font-medium text-[var(--ink-navy)] whitespace-nowrap">{r.label}</td>
                <td className="px-3 py-2 text-center"><CountPill n={r.m?.count} subjectKey={r.subjectKey} /></td>
                {r.countOnly ? (
                  <>
                    <td className="px-3 py-2 text-right">{DIM}</td>
                    <td className="px-3 py-2 text-right">{DIM}</td>
                    <td className="px-3 py-2 text-right">{DIM}</td>
                    <td className="px-3 py-2 text-right">{DIM}</td>
                    <td className="px-3 py-2 text-right">{DIM}</td>
                  </>
                ) : (
                  <>
                    <td className="px-3 py-2 text-right">{goldCell(r.m)}</td>
                    <td className="px-3 py-2 text-right">{caratCell(r.m?.diamond_ct, r.m?.diamond_pcs)}</td>
                    <td className="px-3 py-2 text-right">{caratCell(r.m?.cs_ct, r.m?.cs_pcs)}</td>
                    <td className="px-3 py-2 text-right">{caratCell(r.m?.pearl_ct, r.m?.pearl_pcs)}</td>
                    <td className="px-3 py-2 text-right">{r.noValue ? DIM : valueCell(r.m?.value, canView)}</td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export const DigestView = ({ payload, titlePrefix = 'Daily Digest', onDrill, sparklines, sparkWindow }) => {
  if (!payload) return <div className="text-slate-500 py-6 text-sm">No digest data.</div>;
  const { period = {}, scope, can_view_values: canView, subjects = [] } = payload;
  const allCompanies = payload.companies || [];
  const isGlobal = scope === 'global';
  const shown = isGlobal ? allCompanies.filter((c) => c.has_activity) : allCompanies;
  const silentCount = isGlobal ? allCompanies.length - shown.length : 0;

  return (
    <div data-testid="digest-view">
      <div className="mb-1 font-serif-display text-[20px] text-[var(--ink-navy)]" style={{ fontFamily: 'Gloock, serif' }}>
        {titlePrefix} - {period.label}
      </div>
      <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-slate-500">
        <span className="inline-flex items-center gap-1"><CalendarDays size={12} /> Activity summary for {period.label}</span>
        {sparkWindow?.start && sparkWindow?.end && (
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-[var(--divider)] bg-white px-2.5 py-1 text-[11px] leading-none text-slate-500"
            title={`Tile sparklines show the ${sparkWindow.label || 'recent trend'} (${fmtSpark(sparkWindow.start)} – ${fmtSpark(sparkWindow.end)}), ending on your selected date`}
            data-testid="digest-spark-window"
          >
            <TrendingUp size={12} className="text-[var(--gold-accent)]" />
            <span className="uppercase tracking-[0.08em] text-[10px] text-slate-400">Tile trend</span>
            <span className="font-mono font-medium text-[var(--ink-navy)]">{fmtSpark(sparkWindow.start)} <span className="text-[var(--gold-accent)]">→</span> {fmtSpark(sparkWindow.end)}</span>
          </span>
        )}
        {scope === 'sales' && payload.sales_customer_count != null && (
          <span className="inline-flex items-center gap-1 text-[var(--gold-icon)]">
            <Info size={12} /> Limited to your {payload.sales_customer_count} attributed customer{payload.sales_customer_count === 1 ? '' : 's'}
          </span>
        )}
        {!canView && <span className="inline-flex items-center gap-1"><Info size={12} /> Values hidden (no permission)</span>}
      </div>

      {shown.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--divider)] bg-[var(--paper-warm)] px-4 py-8 text-center text-sm text-slate-500" data-testid="digest-empty">
          No activity recorded in this period.
        </div>
      ) : (
        shown.map((c) => (
          <CompanySection key={c.company_id || c.company_name} company={c} subjects={subjects} canView={canView} onDrill={onDrill} sparklines={sparklines} />
        ))
      )}

      {silentCount > 0 && (
        <div className="mt-1 text-[11px] text-slate-400" data-testid="digest-silent-note">
          {silentCount} other {silentCount === 1 ? 'company' : 'companies'} had no activity in this period.
        </div>
      )}
    </div>
  );
};

export default DigestView;
